import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle2, Send, Play, AlertTriangle, UserPlus, FileText, Music, Image as ImageIcon, ChevronDown, ChevronUp, Link as LinkIcon, Film, Box, Wrench, Layers, Lightbulb, Sparkles } from 'lucide-react';
import InlineSubmitButton from '@/components/InlineSubmitButton';
import { rawMaterialCounts, BUCKET_LABEL, BUCKET_SHORT } from '@/lib/rawMaterialMap';
import AuthImage from '@/components/AuthImage';

const BUCKET_ICON = {
  scripts:         FileText,
  reference_links: LinkIcon,
  concept_art:     ImageIcon,
  vo:              Music,
  animatics:       Film,
  asset_build:     Box,
  rigging:         Wrench,
  shading:         Sparkles,
  layout:          Layers,
  animation:       Film,
  lighting:        Lightbulb,
  fx_comp:         Sparkles,
};

const PIPELINE_TABS = [
  { key: 'script', label: 'SCRIPT', color: '#94a3b8' },
  { key: 'animatics', label: 'ANIMATICS', color: '#a855f7' },
  { key: 'cba', label: 'CBA', color: '#8b5cf6' },
  { key: 'concept_art', label: 'CONCEPT ART', color: '#d946ef' },
  { key: 'asset_build', label: 'ASSET BUILD', color: '#06b6d4' },
  { key: 'rigging', label: 'RIGGING', color: '#f59e0b' },
  { key: 'layout', label: 'LAYOUT', color: '#0ea5e9' },
  { key: 'animation', label: 'ANIMATION', color: '#3b82f6' },
  { key: 'lighting', label: 'LIGHTING', color: '#8b5cf6' },
  { key: 'fx', label: 'FX', color: '#f97316' },
  { key: 'comp', label: 'COMP', color: '#ec4899' },
  { key: 'sound', label: 'SOUND', color: '#22c55e' },
  { key: 'render', label: 'RENDER', color: '#facc15' },
  { key: 'lrc', label: 'LRC', color: '#14b8a6' },
];

export default function VendorTasksPage() {
  const { api } = useAuth();
  const [grouped, setGrouped] = useState({});
  const [team, setTeam] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [updating, setUpdating] = useState(null);
  const [refs, setRefs] = useState({ concept_art: [], vo: [] });
  const [expandedTask, setExpandedTask] = useState(null);
  const [projectFilter, setProjectFilter] = useState('all'); // 'all' | project_id

  const load = () => {
    api.get('/vendor/my-tasks-by-type').then(r => setGrouped(r.data)).catch(() => {});
    api.get('/vendor/my-team').then(r => setTeam(r.data.invited_artists || [])).catch(() => {});
    api.get('/me/references').then(r => setRefs(r.data || { concept_art: [], vo: [] })).catch(() => {});
  };
  useEffect(() => { load(); }, [api]);

  const updateTask = async (taskId, status, progress) => {
    setUpdating(taskId);
    try {
      await api.put(`/tasks/${taskId}`, { status, progress });
      await load();
    } catch (e) {
      alert(`Failed to update task: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUpdating(null);
    }
  };

  const assignArtist = async (taskId, artistId) => {
    if (!artistId) return;
    setUpdating(taskId);
    try {
      await api.post(`/vendor/tasks/${taskId}/assign-artist`, { artist_id: artistId });
      await load();
    } catch (e) {
      alert(`Failed to assign artist: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUpdating(null);
    }
  };

  // Build the project list from tasks so the filter dropdown only shows
  // projects this vendor is actually assigned on.
  const allTasksUnfiltered = Object.values(grouped).flat();
  const availableProjects = useMemo(() => {
    const seen = new Map();
    for (const t of allTasksUnfiltered) {
      if (t.project_id && !seen.has(t.project_id)) {
        seen.set(t.project_id, t.project_name || t.project_id);
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped]);

  // Apply project filter then dept-tab filter
  const projectScoped = projectFilter === 'all'
    ? allTasksUnfiltered
    : allTasksUnfiltered.filter(t => t.project_id === projectFilter);
  const groupedScoped = useMemo(() => {
    if (projectFilter === 'all') return grouped;
    const out = {};
    for (const [k, arr] of Object.entries(grouped)) {
      const filtered = arr.filter(t => t.project_id === projectFilter);
      if (filtered.length) out[k] = filtered;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped, projectFilter]);
  const allTasks = projectScoped;
  const tasks = activeTab === 'all' ? allTasks : (groupedScoped[activeTab] || []);
  const allTypes = Object.keys(groupedScoped);

  return (
    <div className="space-y-4" data-testid="vendor-tasks-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Assigned Work</h1>
          <p className="text-xs text-zinc-500 mt-1">Tasks assigned by VawWorld Media — organized by department</p>
        </div>
        {availableProjects.length > 1 && (
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Project</label>
            <select
              value={projectFilter}
              onChange={(e) => { setProjectFilter(e.target.value); setActiveTab('all'); }}
              data-testid="vendor-tasks-project-filter"
              className="bg-zinc-900 border border-zinc-700 text-xs text-white rounded-sm h-9 px-3 min-w-[200px] focus:border-amber-400 outline-none"
            >
              <option value="all">All Projects ({availableProjects.length})</option>
              {availableProjects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Pipeline Tabs — only show departments that have tasks */}
      <div className="flex gap-1 border-b border-zinc-800 overflow-x-auto">
        <button onClick={() => setActiveTab('all')} data-testid="vendor-task-tab-all"
          className={`px-4 py-2.5 text-xs font-medium border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap transition-colors ${activeTab === 'all' ? 'border-white text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          ALL <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">{allTasks.length}</span>
        </button>
        {PIPELINE_TABS.filter(tab => (grouped[tab.key] || []).length > 0).map(tab => {
          const count = (grouped[tab.key] || []).length;
          const completed = (grouped[tab.key] || []).filter(t => t.status === 'completed').length;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`vendor-task-tab-${tab.key}`}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap transition-colors ${activeTab === tab.key ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
              style={{ borderBottomColor: activeTab === tab.key ? tab.color : 'transparent' }}>
              {tab.label}
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ backgroundColor: `${tab.color}20`, color: tab.color }}>
                {completed}/{count}
              </span>
            </button>
          );
        })}
        {/* Show other types not in PIPELINE_TABS */}
        {allTypes.filter(t => !PIPELINE_TABS.find(p => p.key === t)).map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 -mb-px capitalize ${activeTab === t ? 'border-zinc-400 text-white' : 'border-transparent text-zinc-600'}`}>
            {t.replace(/_/g, ' ')} ({(grouped[t] || []).length})
          </button>
        ))}
      </div>

      {/* Task List */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="divide-y divide-zinc-800/30">
          {tasks.map(t => {
            const isOverdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed';
            // Per-task raw materials filtered via RAW_MATERIAL_DEPENDENCIES
            // (Sound · VO ≠ Sound · BGM ≠ Animation, etc.)
            const { counts, total, sections } = rawMaterialCounts(t, refs);
            const hasRawMaterials = total > 0;
            const isExpanded = expandedTask === t.id;
            // Build tight pill summary in dependency order
            const pillBits = sections.map(s => {
              const n = counts[s.bucket] ?? s.items.length;
              return `${n} ${BUCKET_SHORT[s.bucket] || s.bucket}${n === 1 ? '' : 's'}`;
            });
            return (
              <div key={t.id} className={`px-4 py-3 ${isOverdue ? 'bg-red-500/5' : ''}`} data-testid={`task-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs text-zinc-200 font-medium">
                        {/* Display per founder spec (2a-c): WO scope title +
                            department, fallback to a sensible label when the
                            scope title is missing on legacy tasks. */}
                        {t.wo_scope_title
                          ? `${t.wo_scope_title} · ${(t.task_type || '').replace(/_/g, ' ')}`
                          : (t.name && t.name !== '(unnamed task)'
                              ? t.name
                              : `${t.parent_code || t.project_name || 'Task'} · ${(t.task_type || '').replace(/_/g, ' ')}`)}
                      </p>
                      <span className={`status-${t.status} px-1.5 py-0.5 rounded-full text-[8px] font-medium`}>{t.status?.replace('_',' ')}</span>
                      {t.work_order_no && <span className="text-[8px] px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded">{t.work_order_no}</span>}
                      {isOverdue && <span className="text-[8px] text-red-400 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />OVERDUE</span>}
                      {t.priority >= 4 && <span className="text-[8px] px-1 py-0.5 bg-red-500/20 text-red-400 rounded">P{t.priority}</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {t.project_name && <span className="text-[10px] text-zinc-400">{t.project_name}</span>}
                      {t.parent_code && <span className="text-[10px] font-mono text-zinc-500">{t.parent_code}</span>}
                      <span className="text-[10px] text-zinc-600">Due: {t.due_date ? new Date(t.due_date).toLocaleDateString() : '-'}</span>
                      <span className="text-[10px] font-mono text-emerald-500">{t.rate_amount?.toLocaleString()}</span>
                      {t.vendor_artist_name && <span className="text-[10px] text-cyan-400">Artist: {t.vendor_artist_name}</span>}
                    </div>
                    {t.status === 'in_progress' && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="w-32 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${t.progress}%` }} />
                        </div>
                        <span className="text-[9px] font-mono text-zinc-500">{t.progress}%</span>
                      </div>
                    )}
                    {/* Raw Materials indicator */}
                    {hasRawMaterials && (
                      <button onClick={() => setExpandedTask(isExpanded ? null : t.id)}
                        data-testid={`raw-materials-toggle-${t.id}`}
                        className="mt-2 flex items-center gap-1.5 text-[9px] text-cyan-400 hover:text-cyan-300 transition-colors">
                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        Raw Materials ({pillBits.join(' · ')})
                      </button>
                    )}
                    {!hasRawMaterials && t.project_id && (
                      <p className="mt-1.5 text-[9px] text-zinc-600 italic">No raw materials uploaded by Production yet for this task type</p>
                    )}
                  </div>
                  <div className="flex gap-1.5 ml-3 items-center">
                    {team.length > 0 && (
                      <label className="flex items-center gap-1 text-[9px] text-zinc-500">
                        <UserPlus className="w-3 h-3" />
                        <select value={t.vendor_artist_id || ''} onChange={e => assignArtist(t.id, e.target.value)} disabled={updating === t.id}
                          data-testid={`vendor-task-artist-select-${t.id}`}
                          className="h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-zinc-300 focus:outline-none">
                          <option value="">Team artist</option>
                          {team.map(a => <option key={a.id} value={a.id}>{a.full_name || a.email}</option>)}
                        </select>
                      </label>
                    )}
                    {/* Inline SUBMIT button (Phase 2 MY TASKS — May 23 2026).
                        Strict canonical-name upload right from the row. */}
                    <InlineSubmitButton task={t} onSubmitted={() => load()} testidPrefix="vendor-row-submit" />
                    {['not_started', 'assigned', 'unassigned'].includes(t.status) && (
                      <button onClick={() => updateTask(t.id, 'in_progress', 10)} disabled={updating === t.id}
                        className="h-7 px-3 bg-blue-500/20 text-blue-400 text-[9px] rounded-sm hover:bg-blue-500/30 flex items-center gap-1">
                        <Play className="w-3 h-3" /> Start
                      </button>
                    )}
                    {/* "Review" and "Done" buttons removed May 24, 2026
                        (founder spec): these legacy controls flipped the
                        task's status to 'review' / 'completed' WITHOUT
                        uploading any file — bypassing the entire 3-gate
                        approval pipeline (Supervisor → PM → Client) and
                        the new nomenclature / FPS / DAG gates. Status now
                        advances through IN REVIEW → REVISION → APPROVED
                        ONLY via the SUBMIT (CBA Clip / Shot) flow, which
                        creates a tracked review_submissions row that
                        supervisors and PMs adjudicate from the Approvals
                        page. There is no longer a way to "mark a task done"
                        without a file going through the gates. */}
                  </div>
                </div>
                {/* Expanded Raw Materials — table-driven sections */}
                {isExpanded && hasRawMaterials && (
                  <div className="mt-3 pl-2 border-l-2 border-cyan-500/20 space-y-3" data-testid={`raw-materials-${t.id}`}>
                    {sections.map(({ bucket, items }) => {
                      const Icon = BUCKET_ICON[bucket] || ImageIcon;
                      if (bucket === 'scripts') {
                        return (
                          <div key={bucket}>
                            <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">{BUCKET_LABEL[bucket]}</p>
                            <div className="space-y-1">
                              {items.map(s => {
                                const wc = (s.script || '').trim() ? s.script.trim().split(/\s+/).length : 0;
                                return (
                                  <details key={s.project_id} className="bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5">
                                    <summary className="flex items-center gap-2 cursor-pointer">
                                      <FileText className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                                      <span className="text-[10px] text-zinc-300 truncate flex-1">{s.project_name}</span>
                                      <span className="text-[9px] text-zinc-500">{wc.toLocaleString()} words</span>
                                      <span className="text-[9px] text-cyan-400">read</span>
                                    </summary>
                                    {s.logline && <p className="text-[10px] text-zinc-300 italic mt-2 mb-1">"{s.logline}"</p>}
                                    {s.synopsis && <p className="text-[10px] text-zinc-300 mt-1 whitespace-pre-wrap">{s.synopsis}</p>}
                                    {s.script && <pre className="text-[10px] text-zinc-300 mt-2 max-h-60 overflow-y-auto whitespace-pre-wrap font-mono leading-relaxed">{s.script}</pre>}
                                  </details>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      if (bucket === 'reference_links') {
                        return (
                          <div key={bucket}>
                            <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">{BUCKET_LABEL[bucket]}</p>
                            <div className="space-y-1">
                              {items.flatMap(p => (p.links || []).map((l, i) => (
                                <a key={`${p.project_id}-${i}`} href={l.url} target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 hover:border-orange-400/40">
                                  <LinkIcon className={`w-3 h-3 flex-shrink-0 ${l.type === 'youtube' ? 'text-rose-400' : l.type === 'mood_board' ? 'text-fuchsia-400' : 'text-orange-400'}`} />
                                  <span className="text-[10px] text-zinc-300 truncate flex-1">{l.title || l.url}</span>
                                  <span className="text-[8px] uppercase tracking-wider text-zinc-500">{l.type}</span>
                                </a>
                              )))}
                            </div>
                          </div>
                        );
                      }
                      // Submission buckets — show first media as thumb
                      return (
                        <div key={bucket}>
                          <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">{BUCKET_LABEL[bucket]} ({items.length})</p>
                          <div className="grid grid-cols-3 gap-1.5">
                            {items.slice(0, 9).map(r => {
                              const firstMedia = (r.media || [])[0] || {};
                              const thumbSrc = firstMedia.url || (firstMedia.storage_key ? `/api/reviews/media?key=${encodeURIComponent(firstMedia.storage_key)}` : '');
                              return (
                                <div key={r.id} className="relative bg-zinc-950 border border-zinc-800 rounded overflow-hidden" data-testid={`raw-${bucket}-${r.id}`}>
                                  {thumbSrc ? (
                                    <AuthImage src={thumbSrc} alt={r.title || ''} className="w-full h-16 object-cover" />
                                  ) : (
                                    <div className="w-full h-16 flex items-center justify-center"><Icon className="w-4 h-4 text-zinc-700" /></div>
                                  )}
                                  <div className="px-1.5 py-1">
                                    <p className="text-[9px] text-zinc-300 truncate">{r.title || 'Untitled'}</p>
                                    <span className={`text-[8px] px-1 py-0.5 rounded-full ${
                                      r.approval_state === 'approved' ? 'bg-emerald-500/15 text-emerald-400' :
                                      r.approval_state === 'changes_requested' ? 'bg-amber-500/15 text-amber-400' :
                                      'bg-blue-500/15 text-blue-400'
                                    }`}>{(r.approval_state || 'pending').replace(/_/g, ' ')}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {tasks.length === 0 && <p className="text-xs text-zinc-600 text-center py-8">No {activeTab} tasks assigned</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Component moved to /app/frontend/src/components/InlineSubmitButton.js
//     (May 23 2026 — DRY refactor — also used by EmployeeDashboard) ────

